import Service, { service } from '@ember/service';
import { getLrapEnrollmentStatusRequest } from 'tio-data/requests/enrollment';
import type { Store } from '@warp-drive/core';
import type { LrapEnrollmentStatus } from 'tio-data/requests/enrollment';

export default class LrapEnrollmentService extends Service {
  @service declare store: Store;

  /**
   * The signed-in user's LRAP enrollment state from
   * `GET /api/v2/lrap-enrollments/status`. Fails open (null) so an outage
   * never breaks the SLR dashboard or locks users out of the enrollment
   * wizard — callers treat null as "state unknown" and keep their current
   * behavior. A 200 whose body doesn't match the contract (e.g. a demo-mode
   * blocked response, or a gateway error page) degrades to the same null
   * rather than being trusted as a status.
   */
  fetchStatus = async () => {
    try {
      const { content } = (await this.store.request(getLrapEnrollmentStatusRequest())) as {
        content: unknown;
      };
      if (!isLrapEnrollmentStatus(content)) {
        console.warn('[LRAP enrollment] Unexpected enrollment-status payload', content);
        return null;
      }
      return content;
    } catch (error) {
      console.warn('[LRAP enrollment] Failed to load enrollment status', error);
      return null;
    }
  };
}

const LRAP_ENROLLMENT_STATUS_VALUES = ['not_eligible', 'not_started', 'enrolled'] as const;

function isLrapEnrollmentStatus(content: unknown): content is LrapEnrollmentStatus {
  if (typeof content !== 'object' || content === null) return false;
  const candidate = content as Record<string, unknown>;
  return (
    typeof candidate['lrapEligible'] === 'boolean' &&
    typeof candidate['status'] === 'string' &&
    (LRAP_ENROLLMENT_STATUS_VALUES as readonly string[]).includes(candidate['status'])
  );
}
